import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { ItemFormModal } from '../components/ItemFormModal';
import type { Item } from '../types/item';
import { api } from '@/shared/api/axios';
import {
  ArrowLeft, Package, BarChart2, Layers, History, Boxes,
  CheckCircle2, AlertCircle, Edit2, X, Loader2, Plus,
} from 'lucide-react';

function fmt(minor: number) { return '₹' + (minor / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 }); }

const TABS = [
  { id: 'overview', label: 'Overview', icon: Package },
  { id: 'pricing', label: 'Pricing', icon: BarChart2 },
  { id: 'units', label: 'Units & Tracking', icon: Layers },
  { id: 'stock', label: 'Stock', icon: Boxes },
  { id: 'audit', label: 'Audit / History', icon: History },
];

const ITEM_TYPE_COLORS: Record<string, string> = {
  stock_item: 'bg-blue-100 text-blue-700', service: 'bg-violet-100 text-violet-700',
  raw_material: 'bg-amber-100 text-amber-700', finished_good: 'bg-green-100 text-green-700',
  media_spend: 'bg-rose-100 text-rose-700', subscription: 'bg-pink-100 text-pink-700',
  consumable: 'bg-yellow-100 text-yellow-700', packaging: 'bg-lime-100 text-lime-700',
};

// ── Opening Stock Wizard ──────────────────────────────────────────────────────
function OpeningStockWizard({ itemId, onClose, onPosted }: { itemId: string; onClose: () => void; onPosted: () => void }) {
  const [form, setForm] = useState({ qty: '', unitCostMinor: '', batchNumber: '', qualityStatus: 'good', expiryDate: '', manufactureDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.qty || !form.unitCostMinor) { setError('Qty and Unit Cost are required'); return; }
    setSaving(true); setError('');
    try {
      await api.post(`/api/accounting/items/${itemId}/opening-stock`, {
        qty: Number(form.qty),
        unitCostMinor: Math.round(parseFloat(form.unitCostMinor) * 100),
        batchNumber: form.batchNumber,
        qualityStatus: form.qualityStatus,
        expiryDate: form.expiryDate || undefined,
        manufactureDate: form.manufactureDate || undefined,
      });
      onPosted();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Opening Stock Entry</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Quantity *</label>
            <input type="number" min="0.001" step="any" required className={inputCls} value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Unit Cost (₹) *</label>
            <input type="number" min="0" step="0.01" required className={inputCls} value={form.unitCostMinor} onChange={e => setForm(f => ({ ...f, unitCostMinor: e.target.value }))} /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Batch / Lot #</label>
            <input className={inputCls} value={form.batchNumber} onChange={e => setForm(f => ({ ...f, batchNumber: e.target.value }))} placeholder="e.g. LOT-001" /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Quality Status</label>
            <select className={inputCls} value={form.qualityStatus} onChange={e => setForm(f => ({ ...f, qualityStatus: e.target.value }))}>
              <option value="good">Good</option><option value="quarantine">Quarantine</option><option value="rejected">Rejected</option>
            </select></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Manufacture Date</label>
            <input type="date" className={inputCls} value={form.manufactureDate} onChange={e => setForm(f => ({ ...f, manufactureDate: e.target.value }))} /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Expiry Date</label>
            <input type="date" className={inputCls} value={form.expiryDate} onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))} /></div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-50 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Record Stock
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Detail Page ───────────────────────────────────────────────────────────────
export function ItemMasterDetailPage() {
  const { itemId: id } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<Item | null>(null);
  const [priceHistory, setPriceHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showOpeningStock, setShowOpeningStock] = useState(false);

  const loadItem = async () => {
    setLoading(true);
    try {
      const [itemRes, histRes] = await Promise.all([
        api.get(`/api/accounting/items/${id}`),
        api.get(`/api/accounting/items/${id}/price-history`),
      ]);
      setItem(itemRes.data.data);
      setPriceHistory(histRes.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (id) loadItem(); }, [id]);

  if (loading) return (
    <div className="">
      <AccountingSubNav />
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    </div>
  );

  if (!item) return (
    <div className="">
      <AccountingSubNav />
      <div className="text-center py-16 text-slate-400">Item not found</div>
    </div>
  );

  const typeColor = ITEM_TYPE_COLORS[item.itemType] || 'bg-slate-100 text-slate-600';

  return (
    <div className="">
      <AccountingSubNav />
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate(-1)} className="mt-1 p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
              <ArrowLeft className="w-4 h-4 text-slate-500" />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{item.name}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${typeColor}`}>{item.itemType.replace(/_/g, ' ')}</span>
                {!item.isActive && <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Inactive</span>}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                <span className="font-mono">{item.sku}</span>
                {item.hsnSac && <span>HSN: {item.hsnSac}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEdit(true)} className="px-3 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1.5 transition-colors text-slate-700 dark:text-slate-300">
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Current Stock', value: item.isStockManaged ? String(item.currentStock) : 'N/A', sub: (item.reorderPoint ?? 0) > 0 ? `Reorder at ${item.reorderPoint}` : '', color: item.isStockManaged && item.currentStock <= (item.reorderPoint ?? 0) ? 'text-amber-600' : 'text-slate-900 dark:text-slate-100' },
            { label: 'Selling Price', value: item.sellingPriceMinor > 0 ? fmt(item.sellingPriceMinor) : '—', sub: '', color: 'text-emerald-600' },
            { label: 'Purchase Price', value: item.purchasePriceMinor > 0 ? fmt(item.purchasePriceMinor) : '—', sub: '', color: 'text-slate-900 dark:text-slate-100' },
            { label: 'Gross Margin', value: item.marginPercent ? item.marginPercent.toFixed(1) + '%' : '—', sub: `Markup: ${item.markupPercent ? item.markupPercent.toFixed(1) + '%' : '—'}`, color: 'text-violet-600' },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="text-xs text-slate-500 mb-1">{s.label}</div>
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              {s.sub && <div className="text-xs text-slate-400 mt-0.5">{s.sub}</div>}
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white dark:bg-slate-900 rounded-xl p-1 border border-slate-200 dark:border-slate-800 shadow-sm overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-5">

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800 pb-2">Item Details</h3>
                  {[
                    ['Name', item.name],
                    ['SKU', item.sku], ['Barcode', item.barcode || '—'], ['HSN/SAC', item.hsnSac || '—'],
                    ['Type', item.itemType.replace(/_/g, ' ')],
                    ['Valuation', item.valuationMethod?.toUpperCase() || '—'],
                  ].map(([k, v]) => (
                    <div key={String(k)} className="flex justify-between">
                      <span className="text-sm text-slate-500">{k}</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800 pb-2">Flags & GL Mappings</h3>
                  {[['Purchased', item.isPurchased], ['Sold', item.isSold], ['Produced', item.isProduced], ['Stock Managed', item.isStockManaged], ['Batch Tracked', item.trackBatch], ['Serial Tracked', item.trackSerial]].map(([k, v]) => (
                    <div key={String(k)} className="flex justify-between">
                      <span className="text-sm text-slate-500">{k}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{v ? 'Yes' : 'No'}</span>
                    </div>
                  ))}
                  {item.glInventoryAccount && <div className="flex justify-between"><span className="text-sm text-slate-500">Inventory GL</span><span className="font-mono text-xs text-slate-700">{item.glInventoryAccount}</span></div>}
                </div>
              </div>
              {item.description && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-2">Description</div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg px-4 py-3">{item.description}</p>
                </div>
              )}
              {/* Service fields */}
              {['service', 'media_spend', 'subscription', 'fixed_deliverable'].includes(item.itemType) && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-100 pb-2 mb-3">Service Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {[['Billable Unit', item.billableUnit || '—'], ['Duration', item.durationMinutes ? item.durationMinutes + ' min' : '—'], ['Required Skill', item.requiredSkill || '—'], ['Timesheet Linked', item.timesheetLinked ? 'Yes' : 'No']].map(([k, v]) => (
                      <div key={String(k)}><div className="text-xs text-slate-400">{k}</div><div className="text-sm font-medium">{v}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PRICING */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                {[['Purchase Price', item.purchasePriceMinor], ['Standard Cost', item.standardCostMinor], ['Selling Price', item.sellingPriceMinor]].map(([label, val]) => (
                  <div key={String(label)} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                    <div className="text-xs text-slate-500 mb-1">{label}</div>
                    <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{Number(val) > 0 ? fmt(Number(val)) : '—'}</div>
                  </div>
                ))}
              </div>
              <div className="bg-indigo-50 dark:from-indigo-950/30 dark:to-violet-950/30 rounded-xl p-5 grid grid-cols-2 gap-6 border border-indigo-100 dark:border-indigo-900">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Gross Margin %</div>
                  <div className="text-3xl font-black text-indigo-600">{item.marginPercent ? item.marginPercent.toFixed(2) : '0.00'}%</div>
                  <div className="text-xs text-slate-400 mt-1">= (Selling − Cost) / Selling × 100</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Markup %</div>
                  <div className="text-3xl font-black text-violet-600">{item.markupPercent ? item.markupPercent.toFixed(2) : '0.00'}%</div>
                  <div className="text-xs text-slate-400 mt-1">= (Selling − Cost) / Cost × 100</div>
                </div>
              </div>
            </div>
          )}

          {/* UNITS & TRACKING */}
          {activeTab === 'units' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[['Valuation Method', item.valuationMethod?.toUpperCase()], ['Lead Time', item.leadTimeDays + ' days'], ['Weight', item.weightKg + ' kg'], ['Volume', item.volumeLiters + ' liters']].map(([k, v]) => (
                  <div key={String(k)}><div className="text-xs text-slate-500">{k}</div><div className="font-medium text-slate-900 dark:text-slate-100">{v}</div></div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-2">Tracking</div>
                <div className="flex gap-2">
                  {[['Batch/Lot', item.trackBatch], ['Serial/IMEI', item.trackSerial], ['Expiry Date', item.trackExpiry]].map(([l, v]) => (
                    <span key={String(l)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${v ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>{String(l)}: {v ? 'Enabled' : 'Off'}</span>
                  ))}
                </div>
              </div>
              {(item.alternateUnits?.length ?? 0) > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-2">Alternate Units</div>
                  <div className="space-y-2">
                    {item.alternateUnits!.map((u, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg px-4 py-2">
                        <span className="text-sm font-medium">{u.unitName || u.unitId}</span>
                        <span className="text-sm text-slate-500">1 unit = {u.conversionFactor} base</span>
                        <div className="flex gap-1">
                          {u.purchaseUom && <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 rounded">Purchase UOM</span>}
                          {u.salesUom && <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Sales UOM</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STOCK */}
          {activeTab === 'stock' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Stock Overview</h3>
                {item.isStockManaged && (
                  <button onClick={() => setShowOpeningStock(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                    <Plus className="w-4 h-4" /> Opening Stock Entry
                  </button>
                )}
              </div>
              {!item.isStockManaged ? (
                <div className="text-center py-8 text-slate-400">
                  <Boxes className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>This item is not stock-managed</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-4">
                    {[['Current Stock', item.currentStock, item.currentStock <= (item.reorderPoint ?? 0) && (item.reorderPoint ?? 0) > 0 ? 'text-amber-600' : 'text-slate-900'],
                      ['Reorder Point', item.reorderPoint, 'text-slate-900'],
                      ['Safety Stock', item.safetyStock, 'text-slate-900'],
                      ['Lead Time', item.leadTimeDays + 'd', 'text-slate-900']].map(([l, v, c]) => (
                      <div key={String(l)} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                        <div className="text-xs text-slate-500">{l}</div>
                        <div className={`text-xl font-bold ${c}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {(item.openingStockEntries?.length ?? 0) > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-600 mb-2">Opening Stock Entries</div>
                      <div className="space-y-2">
                        {item.openingStockEntries!.map((e: any, i: number) => (
                          <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg px-4 py-2.5">
                            <span className="text-sm">{e.qty} units</span>
                            <span className="text-sm text-slate-500">{fmt(e.unitCostMinor)} each</span>
                            {e.batchNumber && <span className="text-xs bg-amber-100 text-amber-700 px-2 rounded">Batch: {e.batchNumber}</span>}
                            <span className={`text-xs px-2 py-0.5 rounded ${e.qualityStatus === 'good' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{e.qualityStatus}</span>
                            {e.postedAt ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-amber-400" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* AUDIT */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700">Price & Cost History</h3>
              {priceHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">No price changes recorded yet</div>
              ) : (
                <div className="space-y-2">
                  {priceHistory.map((h, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{h.field.replace(/Minor$/, '').replace(/([A-Z])/g, ' $1').trim()}</div>
                        <div className="text-xs text-slate-400">{new Date(h.changedAt).toLocaleString('en-IN')}</div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500">{fmt(h.oldValueMinor)}</span>
                        <span className="text-slate-300">→</span>
                        <span className="font-semibold text-indigo-600">{fmt(h.newValueMinor)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showOpeningStock && item && (
        <OpeningStockWizard
          itemId={item._id}
          onClose={() => setShowOpeningStock(false)}
          onPosted={() => { setShowOpeningStock(false); loadItem(); }}
        />
      )}

      <ItemFormModal
        open={showEdit}
        item={item}
        onClose={() => setShowEdit(false)}
        onSaved={updated => { setItem(updated); setShowEdit(false); }}
      />
    </div>
  );
}

export default ItemMasterDetailPage;
